import React, { useState } from 'react';
import { Star } from '@phosphor-icons/react';

// Star display and star input in one component.
//
// Ratings on this platform are stars only - deliberately no text reviews, so
// there is nothing to moderate or render beyond this.
//
// Two modes:
//   readOnly (default) - shows an average, optionally with the rating count.
//   interactive        - the customer picks a rating; `onRate` gets 1-5.
//
// In interactive mode the stars are real <button>s, each at least 44px, so
// they're reachable on a phone - the same touch-target floor the rest of this
// app's controls were brought up to.
const SIZES = {
  small: 14,
  medium: 18,
  large: 28
};

const StarRating = ({
  value = 0,
  count = null,
  size = 'small',
  readOnly = true,
  onRate = null,
  busy = false,
  className = ''
}) => {
  const [hovered, setHovered] = useState(0);
  const pixelSize = SIZES[size] || SIZES.small;

  // While hovering, preview the rating the customer is about to pick.
  const displayed = !readOnly && hovered > 0 ? hovered : value;

  if (readOnly) {
    // A product nobody has rated yet shouldn't show five grey stars as though
    // it scored zero - say so instead.
    if (!count) {
      return <span className={`text-xs text-gray-400 ${className}`}>Not rated yet</span>;
    }

    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <Star size={pixelSize} weight="fill" className="text-amber-500" />
        <span className="text-xs font-medium text-gray-700">{Number(value).toFixed(1)}</span>
        <span className="text-xs text-gray-400">({count})</span>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center ${className}`} role="group" aria-label="Rate this product">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={busy}
          onClick={() => onRate && onRate(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onFocus={() => setHovered(star)}
          onBlur={() => setHovered(0)}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
          aria-pressed={value === star}
          className="w-11 h-11 inline-flex items-center justify-center -mx-0.5 disabled:opacity-50 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-full"
        >
          <Star
            size={pixelSize}
            weight={star <= displayed ? 'fill' : 'regular'}
            className={star <= displayed ? 'text-amber-500' : 'text-gray-300'}
          />
        </button>
      ))}
    </span>
  );
};

export default StarRating;
